import React from 'react';

import { Bone } from '@lightspeed/flame/Bone';
import { Box, Flex } from '@lightspeed/flame/Core';
import axios from 'axios';

import { StyledToolTip } from '../pages/views/devices/DevicesPage/PaymentProcessing/Components.style';

import { InfoIcon, StyledPillBadge } from './Components.style';

export enum LSPaymentHardwareSetupStatus {
  GET_STARTED = 'get_started',
  INCOMPLETE_GATEWAY_INACTIVE_TERMINAL_PAIRED = 'incomplete_gateway_inactive_terminal_paired',
  INCOMPLETE_GATEWAY_ACTIVE_TERMINAL_UNPAIRED = 'incomplete_gateway_active_terminal_unpaired',
  TRANSACTIONAL = 'transactional',
}

const statusDisplayMap: { [key: string]: string } = {
  get_started: 'Get started',
  incomplete_gateway_inactive_terminal_paired: 'Incomplete',
  incomplete_gateway_active_terminal_unpaired: 'Incomplete',
  transactional: 'Transactional',
};

const GetStartedPill = () => (
  <Flex flexDirection="row">
    <StyledPillBadge size="small" backgroundColor="#D6DFFD" borderColor="#829FF5">
      {statusDisplayMap[LSPaymentHardwareSetupStatus.GET_STARTED]}
    </StyledPillBadge>
    <StyledToolTip content="Activate your Lightspeed gateway and pair at least one terminal.">
      <InfoIcon />
    </StyledToolTip>
  </Flex>
);

const IncompletePill = () => (
  <Flex flexDirection="row" alignItems="center">
    <StyledPillBadge size="small" backgroundColor="#FFEDD4" borderColor="#FCC37A">
      {statusDisplayMap[LSPaymentHardwareSetupStatus.INCOMPLETE_GATEWAY_INACTIVE_TERMINAL_PAIRED]}
    </StyledPillBadge>
    <StyledToolTip content="Make sure that your gateway is active and that at least one terminal is paired.">
      <InfoIcon />
    </StyledToolTip>
  </Flex>
);

const TransactionalPill = () => (
  <Flex flexDirection="row" alignItems="center">
    <StyledPillBadge size="small" backgroundColor="#D8FCDC" borderColor="#88F398">
      {statusDisplayMap[LSPaymentHardwareSetupStatus.TRANSACTIONAL]}
    </StyledPillBadge>
    <StyledToolTip content="You’re ready to start processing payments.">
      <InfoIcon />
    </StyledToolTip>
  </Flex>
);

export const useLightspeedPaymentsSetupStatus = (
  shopId?: string
): {
  status: LSPaymentHardwareSetupStatus | null;
  getStatusPill: () => JSX.Element | undefined;
  getStatusMessage: () => string | undefined;
  isLoading: boolean;
  reFetch: () => void;
  isError: boolean;
} => {
  const [status, setStatus] = React.useState<LSPaymentHardwareSetupStatus | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isError, setIsError] = React.useState(false);

  const hardwareSetupStatusEndpoint = `/admin/payments/lightspeed-payments/hardware-setup/status${
    shopId ? `/${shopId}` : ''
  }`;

  const fetchStatus = React.useCallback(async () => {
    setIsLoading(true);

    try {
      const {
        data: {
          data: { status },
        },
      } = await axios.get<{ data: { status: LSPaymentHardwareSetupStatus } }>(
        hardwareSetupStatusEndpoint
      );

      setStatus(status);
      setIsError(false);
    } catch (e) {
      setStatus(null);
      setIsError(true);
      console.warn(e);
    } finally {
      setIsLoading(false);
    }
  }, [hardwareSetupStatusEndpoint]);

  React.useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const getStatusPill = React.useCallback(() => {
    if (isLoading) {
      return (
        <Box>
          <Bone width="120px" height="24px" style={{ borderRadius: '24px' }} />
        </Box>
      );
    }

    switch (status) {
      case LSPaymentHardwareSetupStatus.INCOMPLETE_GATEWAY_INACTIVE_TERMINAL_PAIRED:
      case LSPaymentHardwareSetupStatus.INCOMPLETE_GATEWAY_ACTIVE_TERMINAL_UNPAIRED:
        return <IncompletePill />;
      case LSPaymentHardwareSetupStatus.TRANSACTIONAL:
        return <TransactionalPill />;
      case LSPaymentHardwareSetupStatus.GET_STARTED:
        return <GetStartedPill />;
      default:
        return undefined;
    }
  }, [status, isLoading]);

  const getStatusMessage = React.useCallback(() => {
    switch (status) {
      case LSPaymentHardwareSetupStatus.INCOMPLETE_GATEWAY_INACTIVE_TERMINAL_PAIRED:
        return 'Gateway inactive, terminal(s) paired';
      case LSPaymentHardwareSetupStatus.INCOMPLETE_GATEWAY_ACTIVE_TERMINAL_UNPAIRED:
        return 'Gateway active, terminal(s) not paired';
      case LSPaymentHardwareSetupStatus.TRANSACTIONAL:
        return 'Gateway active, terminal(s) paired';
      case LSPaymentHardwareSetupStatus.GET_STARTED:
        return 'Gateway inactive, terminal(s) not paired';
      default:
        return undefined;
    }
  }, [status]);

  return { status, getStatusPill, getStatusMessage, isLoading, reFetch: fetchStatus, isError };
};
